import React, { useEffect, useState } from 'react'
import { FormattedMessage, FormattedRelativeTime, useIntl } from 'react-intl'
import { selectUnit } from '@formatjs/intl-utils'
import FileIcon from 'components/FileIcon'
import Icon from 'components/Icon'
import useApi from 'hooks/useApi'
import { Transition } from '@headlessui/react'
import messages from './messages'
import { Button } from '@arnsbogroup/react-voicearchive'

const FeedbackButton = ({ onSubmitted, note, customerId, orderId }) => {
  const [feedback, loading, errors, refetch] = useApi('/rest/v2/feedback', { load: false })
  const [nps, setNps] = useState(null)
  const [interimNps, setInterimNps] = useState(0)
  const [showFeedbackMenu, setShowFeedbackMenu] = useState(false)
  const [roles, setRoles] = useState({
    PM: false,
    Voice: false,
    Producer: false,
  })

  const onSubmit = () => {
    refetch({
      method: 'POST',
      json: {
        orderId,
        customerId,
        note,
        nps,
        to: Object.entries(roles).filter(([k, v]) => !!v).map(([k, v]) => k.toUpperCase())
      }
    })

  }

  useEffect(() => {
    if (feedback && !loading) {
      setShowFeedbackMenu(false)

      onSubmitted({
        type: 'Feedback',
        title: note,
        body: note,
        by: { name: 'You', email: '' },
        timestamp: (+Date.now() / 1000) - 3,
        meta: {
          nps,
          to: Object.entries(roles).filter(([k, v]) => !!v).map(([k, v]) => k.toUpperCase())
        },
      })
    }
  }, [loading, feedback])

  return (
    <div className="ml-3 relative">
      <div>
        <Button
          variant={note.length ? 'solid' : 'outline'}
          size='sm'
          disabled={loading || !note.length}
          onClick={() => setShowFeedbackMenu(!showFeedbackMenu)}
          icon={['fa', 'comment-alt-check']}
          iconPosition="right"
        >
          <FormattedMessage {...messages.title } />
        </Button>
      </div>

      <Transition
        show={showFeedbackMenu}
        enter="transition ease-out duration-100"
        enterFrom="transform opacity-0 scale-95"
        enterTo="transform opacity-100 scale-100"
        leave="transition ease-in duration-75"
        leaveFrom="transform opacity-100 scale-100"
        leaveTo="transform opacity-0 scale-95"
        className="origin-top-right absolute right-0 -mt-36 w-72 rounded-sm shadow-md bg-white ring-1 ring-black ring-opacity-5 divide-y divide-gray-100 overflow-hidden"
        role="menu"
        aria-orientation="vertical"
        aria-labelledby="user-menu"
      >
        <div className="w-full relative z-0 inline-flex divide-x divide-gray-100">
          <div className="w-full inline-flex items-center p-2 bg-white text-sm font-medium">
            <Icon icon="user-friends" />
          </div>

          { Object.entries(roles).map((([role, selected]) => (
            <button
              key={role}
              type="button"
              onClick={() => setRoles({ ...roles, [role]: !selected })}
              className={`w-full inline-flex items-center px-4 py-2 bg-white text-sm font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-0 ${selected ? 'shadow-inner text-primary' : ''}`}
            >
              <span>{ role }</span>
              <Icon icon="check ml-1 text-sm" className={selected ? 'text-primary' : 'text-white'} />
            </button>
          )))}
        </div>

        <div className="p-2 w-full flex justify-between" onMouseLeave={() => setInterimNps(nps)}>
          { [...Array(11)].map((_, index) => (
            <Icon
              key={index}
              icon="star"
              className={`${index > interimNps ? `text-gray-300 hover:text-yellow-400` : `text-yellow-400`} cursor-pointer`}
              onMouseEnter={() => setInterimNps(index)}
              onClick={() => setNps(index)}
              title={index}
            />
          )) }
        </div>

        <button
          type="button"
          disabled={!Object.entries(roles).filter(([k, v]) => !!v).length || !nps || loading}
          className={!Object.entries(roles).filter(([k, v]) => !!v).length || !nps ?
              "px-4 py-2 w-full text-sm font-medium bg-gray-100 cursor-not-allowed text-gray-400" :
              "px-4 py-2 w-full text-sm font-medium bg-primary hover:bg-primary-darker text-white"
          }
          onClick={onSubmit}
        >
          { loading ? (
            <span>
              Submitting feedback <Icon icon="spinner-third" className="animate-spin" />
            </span>
          ) : (
            <span>
              Submit feedback <Icon icon={['fal', 'comment-alt-check']} className="font-bold" />
            </span>
          ) }

        </button>
      </Transition>
    </div>
  )
}

export default FeedbackButton
